import { TaskRef } from "@/lib/queue/async-queue";
import { DimensionValueSet } from "@/lib/dimensions";
import { compareDimensionValueSets } from "@/lib/dimensions/helpers";

import { TileInfo } from "./tiled-deck-gl-layer";

type _GetTileImageTaskProps = {
  tile: TileInfo;
  dimensionValues: DimensionValueSet;
};

type _Record = {
  dimensionValues: DimensionValueSet;
  task: _Task;
};

type _Task = TaskRef<HTMLImageElement>;

export class TileImageTaskStore {
  private store = {} as {
    [tileId: string]: _Record[];
  };

  findPendingTask({ tile, dimensionValues }: _GetTileImageTaskProps) {
    const record = this.store[tile.id]?.find((record) => {
      return compareDimensionValueSets(dimensionValues, record.dimensionValues);
    });
    return record?.task;
  }

  registerTask(task: _Task, { tile, dimensionValues }: _GetTileImageTaskProps) {
    this.store[tile.id] ||= [];
    this.store[tile.id].push({ task, dimensionValues });

    task.promise
      .finally(() => this.removeTask(task, tile))
      // Cleanup only: the caller awaits `task.promise` and handles its errors. Without
      // this catch, aborted tile requests (cancelled preloads, tiles leaving the
      // viewport) show up as unhandled rejections.
      .catch(() => {});
  }

  cancelPendingTasksForTile(tile: TileInfo) {
    this.pendingTasksForTile(tile).forEach(({ task }) => {
      task.abortController.abort(
        new DOMException(`cancelled by ${this.constructor.name}`, "AbortError"),
      );
    });
  }

  private pendingTasksForTile(tile: TileInfo) {
    return this.store[tile.id] || [];
  }

  private removeTask(task: _Task, tile: TileInfo) {
    this.store[tile.id] = this.store[tile.id].filter(
      (record) => record.task.id !== task.id,
    );
  }
}
